/**
 * The opportunity matrix: every placed idea as a dot on Impact (across) ×
 * Feasibility (up), split at the admin-set threshold into four quadrants. A
 * dot's shape is always its verdict; its colour is the verdict too (Go and
 * Conditional Go are too close in colour for red–green colour blindness, hence
 * the shapes) or, by choice, its industry: the three industries with the most
 * ideas get a hue each, the rest share "Other". The legend and tooltip name
 * both. Hover or focus shows the idea; a click or Enter opens it. Drawn in pixels from the measured width, so labels keep
 * their size on any screen. The Ideas list is this chart's table view.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { Industry } from '@/api/types';

import type { ColorBy } from './ideaFilters';
import { useIdeaLink } from './ideaUi';
import { type MatrixPoint, industrySlots } from './matrixAxes';
import { colourOf, drawOrder, industryLegend, matrixScale, verdictCounts } from './matrixGeometry';
import { MatrixDot, MatrixFrame, MatrixLegend, MatrixTip } from './MatrixParts';

export { QUADRANTS } from './matrixGeometry';

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => entry && setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** The hovered or focused idea, cleared when the points change; a leave only clears its own dot. */
function useActiveDot(points: MatrixPoint[]) {
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => setActive(null), [points]);
  const deactivate = useCallback((id: string) => setActive((a) => (a === id ? null : a)), []);
  return { active, setActive, deactivate };
}

export function OpportunityMatrix({
  points,
  threshold,
  colorBy = 'verdict',
  industries = [],
  compact = false,
}: {
  points: MatrixPoint[];
  threshold: number;
  colorBy?: ColorBy;
  /** Every industry, for the colour slots when colouring by industry. */
  industries?: Industry[];
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const ideaLink = useIdeaLink();
  const [boxRef, width] = useWidth<HTMLDivElement>();
  const { active, setActive, deactivate } = useActiveDot(points);

  const byIndustry = colorBy === 'industry';
  const slots = industrySlots(industries);
  const scale = matrixScale(width, compact, points, threshold);
  const { activePoint, drawn, sameSpot } = drawOrder(points, active);
  const open = (id: string) => {
    const link = ideaLink(id);
    navigate(link.to, { state: link.state });
  };

  return (
    <div className={`opp-matrix${compact ? ' is-compact' : ''}`}>
      <div className="opp-box" ref={boxRef}>
        {width > 0 && (
          <svg
            width={width}
            height={scale.height}
            role="group"
            aria-label={`Opportunity matrix: ${points.length} ideas by impact and feasibility`}
          >
            <MatrixFrame scale={scale} />
            {drawn.map((p) => (
              <MatrixDot
                key={p.idea.id}
                point={p}
                scale={scale}
                active={p.idea.id === active}
                colour={byIndustry ? colourOf(p, slots) : ''}
                onActivate={setActive}
                onDeactivate={deactivate}
                onOpen={open}
              />
            ))}
          </svg>
        )}
        {activePoint && <MatrixTip point={activePoint} sameSpot={sameSpot} scale={scale} width={width} />}
      </div>
      <MatrixLegend
        byIndustry={byIndustry}
        industryEntries={byIndustry ? industryLegend(points, industries, slots) : []}
        counts={verdictCounts(points)}
      />
    </div>
  );
}
